(ns hai.ui.components.collection.bulk-actions
  "Events and components for collection components, such as tables, that support bulk actions via
  Pathom mutations"
  (:require
   [hai.ui.components :as c]
   [hai.ui.components.collection.utils :as utils]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defeffect defevent defevent-fx defsub]]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.download :as belt.download]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [humbleai.belt.vector :refer [elem-present? toggle-membership]]
   [kitchen-async.promise :as p]
   [lambdaisland.fetch :as fetch]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def default [])

(defn disabled? [entity]
  (->> entity
       :user.entity/permissions
       (not-any? #{:permission.type/admin :permission.type/delete})))

(def Action
  [:map
   [:collection.action/title
    {::translate-keys/from :title}
    :string]
   [:collection.action/mutation
    {:optional true
     ::translate-keys/from :mutation}
    :symbol]
   [:collection.action/mutation-fn
    {:optional true
     ::translate-keys/from :mutation-fn}
    ifn?]
   [:collection.action/disabled?
    {:optional true
     ::translate-keys/from :disabled?}
    ifn?]
   ;; Extra re-frame effects to run after the action's mutations succeed,
   ;; appended to the built-in table-resolve. A vector of `[effect-id value]`
   ;; pairs, same shape as `:resolve`'s `:on-success-fx`.
   [:collection.action/on-success-fx
    {:optional true
     ::translate-keys/from :on-success-fx}
    [:or [:vector :any] ifn?]]
   [:collection.action/alert
    {:optional true
     ::translate-keys/from :alert}
    any?]])

(def ActionableCollection
  [:map
   [:table/selected
    {:default default
     ::translate-keys/from :selected}
    [:vector :any]]
   [:table/bulk-actions
    {:optional true
     ::translate-keys/from :bulk-actions}
    [:vector Action]]
   [:table/on-select
    {:optional true
     ::translate-keys/from :on-select}
    ifn?]
   [:table/show-selected-toggle?
    {:default false
     ::translate-keys/from :show-selected-toggle?}
    :boolean]])

(def ExportableCollection
  [:map
   [:table/export-url
    {:optional true
     ::translate-keys/from :export-url}
    :string]])

(defevent-fx :table.bulk-action/act
  [{:keys [db]} ?table {:collection.action/keys [mutation mutation-fn on-success-fx] :as action}]
  (validate Action action)
  (let [{:table/keys [id] :as table} (utils/->table db ?table)
        selected (get-in db [:collections id :table/selected])
        mutations (if mutation
                    (mapv (fn [entity]
                            (list mutation entity))
                          selected)
                    (mutation-fn selected))]
    {:resolve {:query mutations
               :on-success-fx (into [[:dispatch [:table/resolve table]]]
                                    (if (fn? on-success-fx)
                                      (on-success-fx selected)
                                      on-success-fx))}}))

(defevent :table.bulk-action.alert/show
  [db ?table {:collection.action/keys [mutation mutation-fn] :as action}]
  (let [{:table/keys [id] :as table} (utils/->table db ?table)
        table (assoc table :table/show-alert? true)]
    (assoc-in db [:collections id] table)))

(defevent :table.bulk-action.alert/cancel
  [db ?id]
  (let [id (utils/->id ?id)]
    (assoc-in db [:collections id :table/show-alert?] false)))

(defevent-fx :table.bulk-action.alert/confirm
  [{:keys [db]} ?table action]
  {:dispatch-n [[:table.bulk-action.alert/cancel ?table]
                [:table.bulk-action/act ?table action]]})

(defeffect ::on-select
  [{:table/keys [on-select selected]}]
  (when on-select
    (on-select selected)))

(defevent-fx :table/toggle-row
  [{:keys [db]} ?table item]
  (let [{table-id :table/id :as table} (utils/->table db ?table)
        table (update table :table/selected toggle-membership item)
        db (assoc-in db [:collections table-id] table)]
    {:db db
     ::on-select table}))

(defevent :table/select-rows
  [db ?table rows]
  (let [id (utils/->id ?table)]
    (assoc-in db [:collections id :table/selected] rows)))

(defsub :table/selected
  [?table
   table <- [:table ?table]]
  (get table :table/selected))

(defn all-rows-selected?
  [rows selected]
  (= (count rows)
     (count selected)))

(defsub :table/all-selected?
  [?table
   rows <- [:table/rows ?table]
   selected <- [:table/selected ?table]]
  (all-rows-selected? rows selected))

(defsub :table/any-selected?
  [?table
   selected <- [:table/selected ?table]]
  (not-empty selected))

(defsub :table/selected?
  [?table
   row
   selected <- [:table/selected ?table]]
  (elem-present? selected row))

(defsub :table/bulk-actions
  [?table
   table <- [:table ?table]]
  (:table/bulk-actions table))

(defsub :bulk-action/disabled?
  [?table
   row
   bulk-actions <- [:table/bulk-actions ?table]]
  (some (fn [{:collection.action/keys [disabled?]}]
          (when (ifn? disabled?)
            (disabled? row)))
        bulk-actions))

(defsub :bulk-action/show-alert?
  [?table
   table <- [:table ?table]]
  (get table :table/show-alert?))

(defevent :table/select-all
  [db ?table]
  (let [{:table/keys [id rows selected]} (utils/->table db ?table)]
    (if (all-rows-selected? rows selected)
      (assoc-in db [:collections id :table/selected] [])
      (let [all-selected rows]
        (assoc-in db [:collections id :table/selected] all-selected)))))

(defn show?
  [table]
  (boolean
   (or (:table/bulk-actions table)
       (:table/show-selected-toggle? table))))

(o/defstyled select-checkbox* :div
  ;; Sub component because we want to style checkbox container div but need `c/TableCel` to be the
  ;; top level element.
  :h-100%
  :flex :items-center :justify-center
  ;; Radix Checkbox renders a <button> with cursor:default; that overrides
  ;; the edit-overlay's cursor:pointer. Force pointer on the checkbox so
  ;; the bulk-select target reads as clickable.
  [:button {:cursor "pointer"}]
  ([table row]
   (r/with-let [selected? (<s [:table/selected? table row])
                disabled? (<s [:bulk-action/disabled? table row])]
     [:<>
      [c/Checkbox
       {:checked (boolean @selected?)
        :disabled @disabled?
        :title (if @disabled?
                 "You don't have permission to perform bulk actions"
                 "Select item to perform bulk actions")
        :onCheckedChange (fn [_selected?]
                           (>e [:table/toggle-row table row]))}]])))

;; TODO this should contain the conditional rendering logic, not the table or cards
(defn select-checkbox
  [table row]
  [c/TableCell
   ;; This is a bit of a hack to shrink the table cell
   {:style {:width "0"
            :padding-right "0"}}
   [select-checkbox* table row]])

(o/defstyled select-all-checkbox :div
  :h-100%
  :flex :items-center :justify-center
  :pr-0
  ([table row]
   (r/with-let [all-selected? (<s [:table/all-selected? table])]
     [:<>
      [c/Checkbox
       {:checked (boolean @all-selected?)
        :onCheckedChange (fn [_] (>e [:table/select-all table]))}]])))

(defn header-cell
  [table]
  [c/TableColumnHeaderCell
   {:style {:padding-right "0" :width "1em"}}
   [select-all-checkbox table]])

(o/defstyled cancel-button button/small
  button/light
  ([table action]
   [:<>
    {:on-click (fn [_] (>e [:table.bulk-action.alert/cancel table]))}
    "Cancel"]))

(o/defstyled confirm-button button/small
  button/warning
  ([table action]
   [:<>
    {:on-click (fn [_] (>e [:table.bulk-action.alert/confirm table action]))}
    "Confirm delete"]))

(o/defstyled buttons :div
  :flex {:gap "1rem"})

(o/defstyled content* :div
  :flex :flex-col {:gap "1rem"})

(defn content
  [& children]
  [c/AlertDialogContent
   (into [content*] children)])

(def title c/AlertDialogTitle)

(def description c/AlertDialogDescription)

(defn confirm-dialog
  [{:keys [table action]} & children]
  (-> [content]
      (into children)
      (conj [buttons
             [cancel-button table]
             [confirm-button table action]])))

(defn dropdown
  [{:table/keys [bulk-actions] :as table}]
  (r/with-let [any-selected? (<s [:table/any-selected? table])
               show-alert? (<s [:bulk-action/show-alert? table])]
    (when (and bulk-actions @any-selected?)
      [:<>
       (into [:div]
             (comp
              (filter :collection.action/alert)
              (map (fn [{{:keys [content] :as alert} :collection.action/alert :as action}]
                     (let [table @(<s [:table table])]
                       [c/AlertDialogRoot
                        {:open @show-alert?}
                        [content table action]]))))
             bulk-actions)
       [c/DropdownMenuRoot
        [c/DropdownMenuTrigger
         [c/Button
          {:variant "ghost"}
          "Bulk Actions"
          [c/DropdownMenuTriggerIcon]]]
        (into [c/DropdownMenuContent]
              (map (fn [{:collection.action/keys [title alert] :as action}]
                     [:<>
                      [c/DropdownMenuItem
                       {:onSelect (fn [_]
                                    (if alert
                                      (>e [:table.bulk-action.alert/show table action])
                                      (>e [:table.bulk-action/act table action])))}
                       title]]))
              bulk-actions)]])))

(defmethod lambdaisland.fetch/decode-body :raw
  [content-type response opts]
  (.-body response))

(def downloading?     (r/atom false))
(def download-icon    (icons/mui-icon :outlined :download))
(def downloading-icon (icons/mui-icon :outlined :downloading))

(defn- download-spreadsheet! [url]
  (when-not @downloading?
    (reset! downloading? true)
    (p/let [resp (fetch/get url {:as :raw
                                 :headers {"Accept" "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}})]
      (belt.download/download! "events-export" (:body resp) "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      (reset! downloading? false))))

(defn spreadsheet-export
  [{:table/keys [export-url] :as table}]
  (when export-url
    [c/IconButton
     {:size "1"
      :variant "ghost"
      :on-click (fn [_] (download-spreadsheet! export-url))}
     [(if @downloading?
        downloading-icon
        download-icon)]]))
